/**
 * Rotator Block - Interactivity API View Script
 *
 * One clock, one index, N slots.
 *
 * This script decides *which* child of each slot is the visible one and
 * nothing else. How the change looks belongs to the stylesheet, keyed off
 * data-preset — which is why a fifth preset is a CSS change and never touches
 * this file.
 *
 * The stagger is a CSS transition-delay rather than N JavaScript timers: the
 * script sets --brandy-rotator-delay once per slot at init, then every swap is
 * a single synchronous class toggle across all slots and the browser spreads
 * them out.
 *
 * @package BrandyBlocks
 */

import { store, getContext, getElement } from '@wordpress/interactivity';

// Breathing room between the end of one staggered sweep and the start of the
// next. Without it a hand-edited interval can have slots still settling when
// the following swap begins, which reads as a stutter rather than a rhythm.
const SWEEP_BUFFER = 200;

store( 'brandy/rotator', {
	callbacks: {
		init() {
			const ctx = getContext();
			const { ref } = getElement();

			// Reduced motion never starts the clock. The stylesheet's resting
			// rule is already showing each slot's first child, so leaving now
			// produces a complete, static row.
			if (
				window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches
			) {
				return;
			}

			const slots = Array.from(
				ref.querySelectorAll( '.brandy-rotator-slot' )
			);

			// A slot holding one block has nothing to rotate to. If that is
			// every slot, the block is a static row and wants no timer.
			const rotatable = slots.filter(
				( slot ) => slot.children.length > 1
			);

			if ( ! rotatable.length ) {
				return;
			}

			const duration = ctx.duration || 520;
			const stagger = ctx.stagger || 0;

			// The stagger is per slot position, not per rotatable slot, so a
			// static slot in the middle still occupies its place in the wave.
			slots.forEach( ( slot, i ) => {
				slot.style.setProperty(
					'--brandy-rotator-delay',
					`${ stagger * i }ms`
				);
			} );

			const sweep = duration + stagger * Math.max( slots.length - 1, 0 );
			const period = Math.max( ctx.interval || 3500, sweep + SWEEP_BUFFER );

			// Hand over from the stylesheet's :first-child resting rule to
			// explicit is-active, so the swap has something to move between.
			slots.forEach( ( slot ) => {
				slot.children[ 0 ]?.classList.add( 'is-active' );
			} );
			ref.classList.add( 'is-live' );

			let index = 0;

			/*
			 * Every advance restates the whole slot rather than nudging the two
			 * children it happens to know about.
			 *
			 * The nudging version needed a second timer to strip is-leaving
			 * afterwards, and that timer is re-armed and cancelled on every
			 * advance — so anything that delayed it past the next tick (a
			 * background tab clamps setTimeout to a second, a pause, a short
			 * interval) left the class behind. It then accumulated, and the
			 * moment a child carrying a stale is-leaving became the active one
			 * it rendered at the exit position instead of the visible one: a
			 * row that goes blank after one full round.
			 *
			 * Reconciling instead makes every advance idempotent and
			 * self-healing. Whatever state the slot was left in, afterwards
			 * exactly one child is active and exactly one is leaving, so no
			 * cleanup timer is needed and none of those cases can strand a
			 * class.
			 */
			function advance() {
				const previous = index;
				index += 1;

				rotatable.forEach( ( slot ) => {
					const children = Array.from( slot.children );
					const count = children.length;

					// Each slot wraps against its own length, so a two-child
					// slot beside a three-child one stays on the beat and
					// simply repeats sooner. Nothing ever blanks.
					const active = index % count;
					const leaving = previous % count;

					children.forEach( ( child, i ) => {
						child.classList.toggle( 'is-active', i === active );
						child.classList.toggle(
							'is-leaving',
							i === leaving && i !== active
						);
					} );
				} );
			}

			// Three independent reasons to hold the clock, one place that
			// decides. Anything else drifts into a timer that a second pause
			// reason silently restarts.
			let onScreen = false;
			let tabVisible = document.visibilityState !== 'hidden';
			let hovered = false;
			let timer = 0;

			function sync() {
				const shouldRun = onScreen && tabVisible && ! hovered;

				if ( shouldRun && ! timer ) {
					timer = window.setInterval( advance, period );
					return;
				}

				if ( ! shouldRun && timer ) {
					window.clearInterval( timer );
					timer = 0;
				}
			}

			const observer = new window.IntersectionObserver( ( entries ) => {
				onScreen = entries.some( ( entry ) => entry.isIntersecting );
				sync();
			} );
			observer.observe( ref );

			// Background tabs throttle timers rather than stopping them, which
			// leaves a burst of catch-up swaps on return. Clearing the interval
			// outright means the reader comes back to a settled row.
			const onVisibility = () => {
				tabVisible = document.visibilityState !== 'hidden';
				sync();
			};
			document.addEventListener( 'visibilitychange', onVisibility );

			let onEnter, onLeave;

			if ( ctx.pauseOnHover ) {
				onEnter = () => {
					hovered = true;
					sync();
				};
				onLeave = () => {
					hovered = false;
					sync();
				};
				ref.addEventListener( 'mouseenter', onEnter );
				ref.addEventListener( 'mouseleave', onLeave );
			}

			return () => {
				window.clearInterval( timer );
				observer.disconnect();
				document.removeEventListener( 'visibilitychange', onVisibility );

				if ( onEnter ) {
					ref.removeEventListener( 'mouseenter', onEnter );
					ref.removeEventListener( 'mouseleave', onLeave );
				}
			};
		},
	},
} );
